import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { fetchTorNotschalter, setTorNotschalter } from '../../services/maintenanceService';
import {
    TorNotschalterStand,
    torNotschalterRueckfrage,
    torNotschalterText,
    torZeitpunkt,
} from '../../services/torNotschalter';

/**
 * Der Tor-Notschalter (S5a Stufe 3b) in der Admin-Karte „Bauphase", neben dem
 * Mindest-Build — derselbe Ort, dieselbe Haltung: globaler Normalbetrieb,
 * kein Bauphasen-Knopf.
 *
 * Seit 3b ordnet das Rechnungstor aus dem Buchungsjournal zu und sperrt bei
 * Abweichung; seit 3c baut auch der Rechnungsentwurf aus dem Journal. Der
 * Schalter legt ALLE Ausstellungen und Entwuerfe in allen Tabs zurueck auf
 * den JSON-Weg (Zuordnung und Entwurf aus dem Projekt-JSON, Vergleich nur als
 * Schatten) — fuer eine Stoerung des Journals, nicht fuer eine einzelne
 * Rechnung (Betreiber-Entscheid 2). Er wirkt ohne Neuladen: jede Ausstellung
 * und jeder Entwurfsaufbau liest ihn frisch (issueInvoice,
 * buildProjectServiceLines).
 *
 * Die Rueckfrage steht im Seiten-Dialog und nicht in `window.confirm`: der
 * Browserdialog wird in manchen Umgebungen unterdrueckt (eingebettete
 * Ansichten, „keine weiteren Dialoge") und meldet dann still „abgebrochen" —
 * bei einem Notschalter waere das genau im falschen Moment.
 *
 * Regeln und Texte stehen in torNotschalter.ts, das Reden mit der Datenbank
 * in maintenanceService.ts.
 */

/** Text eines RPC-Fehlers — PostgREST liefert ein Objekt, ein Wurf ein Error. */
const fehlerText = (e: unknown): string => {
    if (e instanceof Error) return e.message;
    if (e && typeof e === 'object' && typeof (e as { message?: unknown }).message === 'string') {
        return (e as { message: string }).message;
    }
    return String(e);
};

export const TorNotschalterKarte: React.FC = () => {
    /** null = noch nicht gelesen. */
    const [stand, setStand] = useState<TorNotschalterStand | null>(null);
    const [busy, setBusy] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    /** Die offene Rueckfrage: der Zielzustand (true = einschalten) oder null. */
    const [frage, setFrage] = useState<boolean | null>(null);

    const laden = useCallback(async () => {
        setStand(await fetchTorNotschalter());
    }, []);

    useEffect(() => {
        void laden();
    }, [laden]);

    const umlegen = useCallback(
        async (an: boolean) => {
            setBusy(true);
            setFehler(null);
            try {
                setStand(await setTorNotschalter(an));
                setFrage(null);
            } catch (e) {
                // Auch PGRST202 („Nr. 177 nicht gefahren") landet hier und bleibt
                // sichtbar stehen — sonst hielte der Administrator den Schalter
                // fuer umgelegt.
                setFehler(`Tor-Notschalter konnte nicht umgelegt werden: ${fehlerText(e)}`);
                await laden();
            } finally {
                setBusy(false);
            }
        },
        [laden],
    );

    const an = stand?.lesbar === true && stand.modus === 'json';

    return (
        <div className="mt-6 pt-5 border-t border-slate-200 space-y-3">
            <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${an ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
                    {an ? <ShieldAlert className="w-5 h-5" /> : <ShieldCheck className="w-5 h-5" />}
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-800">Tor-Notschalter: Zuordnung und Rechnungsentwurf aus JSON (S5a)</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                        {stand === null ? 'Stand wird gelesen …' : torNotschalterText(stand)}
                    </p>
                </div>
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div className="rounded-lg border border-slate-200 px-3 py-2">
                    <dt className="text-[11px] font-bold text-slate-500 uppercase">Rechnungstor</dt>
                    <dd className={`mt-0.5 font-semibold ${an ? 'text-red-600' : 'text-slate-700'}`}>
                        {stand === null ? '…' : an ? 'JSON-Modus (Notschalter AN)' : 'Journal-Modus'}
                    </dd>
                </div>
                <div className="rounded-lg border border-slate-200 px-3 py-2">
                    <dt className="text-[11px] font-bold text-slate-500 uppercase">An seit</dt>
                    <dd className="mt-0.5 font-mono tabular-nums text-slate-700">
                        {an && stand?.seit ? torZeitpunkt(stand.seit) : '—'}
                    </dd>
                </div>
                <div className="rounded-lg border border-slate-200 px-3 py-2">
                    <dt className="text-[11px] font-bold text-slate-500 uppercase">Eingeschaltet von</dt>
                    <dd className="mt-0.5 text-slate-700">{an ? stand?.von ?? 'unbekannt' : '—'}</dd>
                </div>
            </dl>

            {frage !== null ? (
                <div
                    role="alertdialog"
                    aria-label="Rückfrage Tor-Notschalter"
                    className={`rounded-lg border px-3 py-3 text-xs space-y-3 ${frage ? 'border-red-300 bg-red-50 text-red-900' : 'border-amber-300 bg-amber-50 text-amber-900'}`}
                >
                    <p>{torNotschalterRueckfrage(frage)}</p>
                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            onClick={() => {
                                void umlegen(frage);
                            }}
                            disabled={busy}
                            className={`${frage ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-800 hover:bg-slate-900'} disabled:opacity-60 text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow-sm transition-colors`}
                        >
                            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                            {frage ? 'Ja, Notschalter einschalten' : 'Ja, Notschalter ausschalten'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setFrage(null)}
                            disabled={busy}
                            className="bg-white hover:bg-slate-50 disabled:opacity-60 text-slate-700 border border-slate-300 px-4 py-2 rounded-lg text-xs font-bold shadow-sm transition-colors"
                        >
                            Abbrechen
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex flex-wrap items-center gap-3">
                    {/* Unlesbar: beide Richtungen anbieten — die RPC ist
                        idempotent, und gerade dann braucht der Administrator
                        den Knopf am dringendsten. */}
                    {(!an || stand?.lesbar !== true) && (
                        <button
                            type="button"
                            onClick={() => setFrage(true)}
                            disabled={busy || stand === null}
                            className="bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow-sm transition-colors"
                        >
                            <ShieldAlert className="w-4 h-4" />
                            Notschalter einschalten (Zuordnung und Entwurf aus JSON)
                        </button>
                    )}
                    {(an || stand?.lesbar !== true) && (
                        <button
                            type="button"
                            onClick={() => setFrage(false)}
                            disabled={busy || stand === null}
                            className="bg-white hover:bg-slate-50 disabled:opacity-60 text-slate-700 border border-slate-300 px-4 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow-sm transition-colors"
                        >
                            <ShieldCheck className="w-4 h-4" />
                            Notschalter ausschalten (zurück zum Journal)
                        </button>
                    )}
                    <span className="text-[11px] text-slate-400">
                        Nur bei einer Störung des Buchungsjournals. Wirkt sofort für alle, ohne Neuladen.
                    </span>
                </div>
            )}

            {fehler && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{fehler}</p>
            )}
        </div>
    );
};
